import { createClient } from '@supabase/supabase-js';
import { UserRole } from '../admin/utils/permissions';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { supabase } from './supabase';

// Định nghĩa interface cho User
export interface User {
  id: string;
  email: string;
  name?: string;
  full_name?: string;
  avatar?: string;
  role?: string;
  is_verified?: boolean;
  is_premium?: boolean;
  subscription_plan?: string;
  subscription_status?: string;
  subscription_end_date?: string;
  ai_credits?: number;
  ai_credits_used?: number;
  total_learning_time?: number;
  created_at?: string;
  updated_at?: string;
  bio?: string;
  preferred_language?: string;
  learning_path?: string;
  areas_of_interest?: string[];
  meditation_experience?: string;
  buddhist_school?: string;
}

// Định nghĩa interface cho token
interface DecodedToken {
  sub?: string;  // Supabase sử dụng sub để lưu ID người dùng
  email?: string;
  userId?: string;
  role?: string;
  exp: number;
  aud?: string;
}

// Lưu token vào localStorage
export const setToken = (token: string) => {
  localStorage.setItem('token', token);
  localStorage.setItem('refreshToken', token); // Trong thực tế, refresh token sẽ khác với access token
};

// Lấy token từ localStorage
export const getToken = () => {
  return localStorage.getItem('token');
};

// Lấy refresh token từ localStorage
export const getRefreshToken = () => {
  return localStorage.getItem('refreshToken');
};

// Xóa token khỏi localStorage
export const removeToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
};

// Kiểm tra token có hợp lệ không
export const isTokenValid = () => {
  const token = getToken();
  if (!token) return false;

  try {
    const decoded = jwtDecode<DecodedToken>(token);
    
    // Kiểm tra thời hạn token
    const isExpired = decoded.exp * 1000 < Date.now();
    if (isExpired) {
      console.warn('Token has expired');
      removeToken(); // Xóa token hết hạn
      return false;
    }
    
    // Kiểm tra các thông tin cần thiết (Supabase JWT sử dụng sub thay vì userId)
    const userId = decoded.sub || decoded.userId;
    if (!userId || !decoded.email) {
      console.warn('Token is missing required fields');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

// Refresh token
export const refreshToken = async () => {
  try {
    const refreshToken = getRefreshToken();
    
    // Đầu tiên thử lấy session hiện tại từ Supabase
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionData?.session) {
      console.log('Đã lấy session từ Supabase:', sessionData.session.user.email);
      
      // Lưu token mới vào localStorage
      localStorage.setItem('token', sessionData.session.access_token);
      localStorage.setItem('refreshToken', sessionData.session.refresh_token);
      return sessionData.session.access_token;
    }
    
    // Nếu không có session và không có refresh token, trả về null thay vì throw lỗi
    if (!refreshToken) {
      console.warn('Không có refresh token và không có session hiện tại');
      return null;
    }

    // Thử refresh token với Supabase
    try {
      console.log('Đang refresh token với Supabase');
      const { data, error } = await supabase.auth.refreshSession({ refresh_token: refreshToken });
      
      if (error || !data.session) {
        console.error('Lỗi khi refresh session với Supabase:', error);
        return null; // Trả về null thay vì throw lỗi
      }
      
      console.log('Refresh token thành công với Supabase');
      // Lưu token mới vào localStorage
      localStorage.setItem('token', data.session.access_token);
      localStorage.setItem('refreshToken', data.session.refresh_token);
      return data.session.access_token;
    } catch (supabaseError) {
      console.warn('Supabase refresh failed, trying API:', supabaseError);
      
      // Nếu Supabase thất bại, thử với API
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        console.log('Refreshing token with API URL:', apiUrl);

        // Gọi API để refresh token
        const response = await axios.post('/api/auth/refresh-token', { refreshToken });
        
        if (response.status === 200 && response.data.success && response.data.token) {
          setToken(response.data.token);
          return response.data.token;
        }
        
        return null; // Trả về null thay vì throw lỗi
      } catch (apiError) {
        console.error('API refresh failed:', apiError);
        return null; // Trả về null thay vì throw lỗi
      }
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
    
    // Xử lý lỗi chi tiết hơn
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Lỗi từ server (status code không phải 2xx)
        console.error('Server error:', error.response.status, error.response.data);
        if (error.response.status === 404) {
          console.error('API endpoint not found. Check server configuration.');
        } else if (error.response.status === 401) {
          console.error('Authentication failed. Token may be invalid or expired.');
        }
      } else if (error.request) {
        // Không nhận được response
        console.error('No response received:', error.request);
      } else {
        // Lỗi khi thiết lập request
        console.error('Request setup error:', error.message);
      }
    }
    
    removeToken();
    return null; // Trả về null thay vì throw lỗi
  }
};

// Interface cho dữ liệu đăng ký
export interface RegisterData {
  email: string;
  password: string;
  full_name: string;
}

// Interface cho dữ liệu đăng nhập
export interface LoginData {
  email: string;
  password: string;
}

/**
 * Đăng ký người dùng mới
 * @param data Dữ liệu đăng ký
 * @returns Thông tin người dùng và token
 */
export const register = async (data: RegisterData) => {
  try {
    // Xác thực dữ liệu đầu vào
    if (!data.email || !data.email.trim()) {
      throw new Error('Email không được để trống');
    }
    
    // Kiểm tra định dạng email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      throw new Error('emailInvalid');
    }
    
    // Kiểm tra độ dài mật khẩu
    if (!data.password || data.password.length < 6) {
      throw new Error('passwordTooShort');
    }
    
    // Kiểm tra tên hiển thị
    if (!data.full_name || data.full_name.trim().length < 3 || data.full_name.trim().length > 50) {
      throw new Error('invalidDisplayName');
    }
    
    // Kiểm tra ký tự đặc biệt trong tên
    const nameSpecialCharsRegex = /[<>{}[\]\\\/]/;
    if (nameSpecialCharsRegex.test(data.full_name)) {
      throw new Error('displayNameContainsInvalidChars');
    }

    console.log('Dữ liệu đăng ký hợp lệ, đang đăng ký người dùng với email:', data.email);
    
    // Kiểm tra bảng users có tồn tại không và cấu trúc của nó
    try {
      const { error: tableCheckError } = await supabase
        .from('users')
        .select('id, username, referral_code')
        .limit(1);
      
      if (tableCheckError) {
        console.error('Lỗi khi kiểm tra bảng users:', tableCheckError);
        if (tableCheckError.message.includes('does not exist') || 
            tableCheckError.message.includes('không tồn tại')) {
          console.error('Bảng users không tồn tại trong schema public');
          // Tiếp tục với đăng ký mà không insert vào bảng users
        }
      } else {
        console.log('Bảng users tồn tại và có thể truy cập');
      }
    } catch (tableError) {
      console.warn('Không thể kiểm tra bảng users:', tableError);
      // Tiếp tục với đăng ký
    }
    
    // Kiểm tra người dùng đã tồn tại trước chưa trong bảng users
    try {
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id')
        .eq('email', data.email)
        .maybeSingle();
        
      if (existingUser) {
        console.log('Email đã tồn tại trong bảng users:', existingUser);
        throw new Error('emailInUse');
      }
    } catch (checkError) {
      // Bỏ qua lỗi khi kiểm tra, tiếp tục quá trình đăng ký
      console.log('Lỗi khi kiểm tra email tồn tại, tiếp tục đăng ký:', checkError);
    }
    
    // Tạo referral code
    const generateReferralCode = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let code = '';
      for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return code;
    };
    
    const referralCode = generateReferralCode();
    
    // Đăng ký người dùng với Supabase Auth - QUAN TRỌNG: Sử dụng payload tối thiểu để tránh lỗi
    const authResponse = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      // Không thêm options hoặc metadata để tránh lỗi database
    });

    if (authResponse.error) {
      console.error('Lỗi đăng ký trong Supabase Auth:', authResponse.error);
      
      // Phân tích lỗi chi tiết từ Supabase
      if (authResponse.error.message.includes('already registered')) {
        throw new Error('emailInUse');
      } else if (authResponse.error.message.includes('password')) {
        throw new Error('passwordTooShort');
      } else if (authResponse.error.message.includes('Database error')) {
        throw new Error('databaseError');
      }
      
      throw authResponse.error;
    }

    if (!authResponse.data.user) {
      console.error('Không nhận được thông tin người dùng từ Supabase Auth');
      throw new Error('Không thể tạo tài khoản');
    }

    console.log('Đăng ký auth thành công, đang thêm thông tin vào bảng users');
    console.log('User ID:', authResponse.data.user.id);
    
    // Cập nhật metadata cho user (sau khi đã tạo thành công)
    try {
      await supabase.auth.updateUser({
        data: {
          full_name: data.full_name.trim(),
          avatar_url: '/assets/images/default-avatar.png',
          referral_code: referralCode
        }
      });
      console.log('Đã cập nhật metadata cho user sau khi tạo thành công');
    } catch (metadataError) {
      console.warn('Không thể cập nhật metadata cho user, nhưng đăng ký vẫn thành công:', metadataError);
    }
    
    // Tạo username từ email hoặc full_name
    let username = data.email.split('@')[0]; // Lấy phần trước @ của email
    // Loại bỏ ký tự đặc biệt, chỉ giữ chữ cái, số và gạch dưới
    username = username.replace(/[^a-zA-Z0-9_]/g, '');
    
    // Nếu username quá ngắn, dùng full_name
    if (username.length < 3) {
      username = data.full_name.trim().toLowerCase().replace(/\s+/g, '_');
      username = username.replace(/[^a-zA-Z0-9_]/g, '');
    }
    
    // Giới hạn độ dài username
    if (username.length > 20) {
      username = username.substring(0, 20);
    }
    
    // Thêm số ngẫu nhiên nếu username quá ngắn
    if (username.length < 3) {
      username = `user_${Math.floor(Math.random() * 10000)}`;
    }
    
    // Thử thêm người dùng vào bảng users riêng biệt
    // Nhưng đừng để lỗi ở đây ngăn cản quá trình đăng ký
    // Vì đã tạo thành công tài khoản auth
    try {
      const now = new Date().toISOString();
      
      // Trước tiên thử với phiên bản rút gọn
      const minimalUserData = {
        id: authResponse.data.user.id,
        email: data.email,
        full_name: data.full_name.trim()
      };
      
      console.log('Thử thêm dữ liệu tối thiểu vào bảng users:', minimalUserData);
      
      // Thử insert dữ liệu tối thiểu trước
      const { error: minimalError } = await supabase
        .from('users')
        .insert(minimalUserData);
      
      if (minimalError) {
        console.warn('Không thể thêm thông tin tối thiểu vào bảng users:', minimalError);
      } else {
        console.log('Đã thêm thông tin người dùng tối thiểu thành công, thử cập nhật thêm thông tin');
        
        // Nếu thêm thành công, cập nhật thêm thông tin
        const additionalData = {
          password: null, // Không lưu password trong bảng users
          role: 'member',
          status: 'active',
          created_at: now,
          last_sign_in_at: now,
          buddhist_school: '',
          subscription_plan: 'free',
          subscription_start_date: null,
          subscription_end_date: null,
          ai_credits: 100,
          last_activity_date: now,
          is_premium: false,
          avatar_url: '/assets/images/default-avatar.png',
          referral_code: referralCode,
          streak: 0,
          level: 1,
          badges: 0,
          username: username
        };
        
        const { error: updateError } = await supabase
          .from('users')
          .update(additionalData)
          .eq('id', authResponse.data.user.id);
        
        if (updateError) {
          console.warn('Không thể cập nhật thông tin bổ sung cho user:', updateError);
          // Vẫn xem như đăng ký thành công vì đã có record cơ bản
        } else {
          console.log('Đã cập nhật đầy đủ thông tin cho user trong bảng users');
        }
      }
    } catch (userDbError) {
      console.warn('Lỗi khi thêm dữ liệu vào bảng users, nhưng vẫn xem như đăng ký thành công:', userDbError);
    }
    
    // Lưu token vào localStorage và xem như đăng ký thành công
    if (authResponse.data.session) {
      localStorage.setItem('token', authResponse.data.session.access_token);
      localStorage.setItem('refreshToken', authResponse.data.session.refresh_token);
    }
    
    return {
      user: authResponse.data.user,
      session: authResponse.data.session,
    };
  } catch (error: any) {
    console.error('Lỗi đăng ký:', error);
    
    // Trả về message dễ hiểu cho phía client xử lý
    if (error.message === 'emailInUse' ||
        error.message === 'passwordTooShort' ||
        error.message === 'invalidDisplayName' ||
        error.message === 'displayNameContainsInvalidChars' ||
        error.message === 'emailInvalid' ||
        error.message === 'invalidRole' ||
        error.message === 'databaseError') {
      throw new Error(error.message);
    }
    
    // Các lỗi khác từ Supabase
    if (error.message?.includes('already registered') || 
        error.message?.includes('unique constraint') || 
        error.message?.includes('duplicate')) {
      throw new Error('emailInUse');
    } else if (error.message?.includes('password')) {
      throw new Error('passwordTooShort');
    } else if (error.message?.includes('Database error')) {
      throw new Error('databaseError');
    }
    
    // Trường hợp còn lại, giữ nguyên lỗi
    throw error;
  }
};

/**
 * Đăng nhập người dùng
 * @param data Dữ liệu đăng nhập
 * @returns Thông tin người dùng và token
 */
export const login = async (data: LoginData) => {
  try {
    // Đăng nhập với Supabase Auth
    const authResponse = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (authResponse.error) {
      throw authResponse.error;
    }

    if (!authResponse.data.user) {
      throw new Error('Đăng nhập thất bại');
    }

    // Lưu token vào localStorage
    if (authResponse.data.session) {
      localStorage.setItem('token', authResponse.data.session.access_token);
      localStorage.setItem('refreshToken', authResponse.data.session.refresh_token);
      localStorage.setItem('user', JSON.stringify(authResponse.data.user));
    }

    // Lấy thông tin người dùng từ bảng users
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', authResponse.data.user.id)
      .single();

    if (userError) {
      console.error('Lỗi đăng nhập:', userError);
      
      // Kiểm tra xem người dùng có tồn tại trong bảng users không
      const { data: userByEmail, error: emailError } = await supabase
        .from('users')
        .select('*')
        .eq('email', authResponse.data.user.email)
        .single();
      
      if (emailError) {
        // Nếu không tìm thấy người dùng, tạo mới trong bảng users
        console.log('Tạo mới người dùng trong bảng users');
        
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert({
            id: authResponse.data.user.id,
            email: authResponse.data.user.email,
            full_name: authResponse.data.user.user_metadata?.full_name || 'Người dùng mới',
            role: 'author',
            status: 'active',
            created_at: new Date().toISOString(),
            last_sign_in_at: new Date().toISOString()
          })
          .select();
        
        if (insertError) {
          console.error('Lỗi tạo người dùng mới:', insertError);
          throw new Error('Không thể tạo thông tin người dùng');
        }
        
        return {
          user: newUser[0],
          session: authResponse.data.session,
        };
      }
      
      // Nếu tìm thấy người dùng bằng email nhưng ID khác, cập nhật ID
      if (userByEmail && userByEmail.id !== authResponse.data.user.id) {
        console.log('Cập nhật ID người dùng');
        
        // Kiểm tra xem ID cũ có được tham chiếu trong bảng posts không
        const { data: posts, error: postsError } = await supabase
          .from('posts')
          .select('id')
          .eq('author_id', userByEmail.id);
        
        if (!postsError && posts && posts.length > 0) {
          // Cập nhật author_id trong bảng posts
          for (const post of posts) {
            await supabase
              .from('posts')
              .update({ author_id: authResponse.data.user.id })
              .eq('id', post.id);
          }
        }
        
        // Xóa bản ghi cũ
        await supabase
          .from('users')
          .delete()
          .eq('id', userByEmail.id);
        
        // Tạo bản ghi mới với ID từ auth.users
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert({
            id: authResponse.data.user.id,
            email: userByEmail.email,
            full_name: userByEmail.full_name,
            role: userByEmail.role,
            status: userByEmail.status,
            created_at: userByEmail.created_at,
            last_sign_in_at: new Date().toISOString()
          })
          .select();
        
        if (insertError) {
          console.error('Lỗi tạo người dùng mới:', insertError);
          throw new Error('Không thể cập nhật thông tin người dùng');
        }
        
        return {
          user: newUser[0],
          session: authResponse.data.session,
        };
      }
      
      throw new Error('Không tìm thấy thông tin người dùng');
    }

    // Cập nhật thời gian đăng nhập gần nhất
    await supabase
      .from('users')
      .update({ last_sign_in_at: new Date().toISOString() })
      .eq('id', authResponse.data.user.id);

    return {
      user: userData,
      session: authResponse.data.session,
    };
  } catch (error) {
    console.error('Lỗi đăng nhập:', error);
    throw error;
  }
};

/**
 * Đăng xuất người dùng
 * @param scope Phạm vi đăng xuất ('local' chỉ xóa dữ liệu trên thiết bị hiện tại, 'global' đăng xuất khỏi tất cả các thiết bị)
 */
export const logout = async (scope: 'local' | 'global' = 'local') => {
  try {
    // Luôn xóa dữ liệu trong localStorage trước để đảm bảo trạng thái mặc định là đăng xuất
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Xử lý đăng xuất từ Supabase
    try {
      // Kiểm tra session hiện tại trước
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.warn('Không thể lấy session hiện tại:', sessionError);
        return true; // Vẫn trả về thành công vì đã xóa dữ liệu local
      }
      
      // Nếu có session, thực hiện đăng xuất
      if (sessionData && sessionData.session) {
        try {
          const { error } = await supabase.auth.signOut({
            scope: scope
          });
          
          if (error) {
            console.error('Lỗi khi đăng xuất khỏi Supabase:', error);
            // Không throw exception ở đây, đã xóa dữ liệu local nên vẫn coi là đăng xuất thành công
          }
        } catch (signOutError) {
          // Xử lý lỗi "Auth session missing" hoặc lỗi 403 Forbidden 
          console.warn('Lỗi khi signOut:', signOutError);
          // Không throw lỗi, vẫn trả về true vì đã xóa dữ liệu local
        }
      } else {
        console.log('Không có session hiện tại, chỉ xóa dữ liệu local');
      }
    } catch (supabaseError) {
      console.error('Lỗi khi đăng xuất khỏi Supabase:', supabaseError);
      // Không throw exception, đã xóa dữ liệu local
    }
    
    return true;
  } catch (error) {
    console.error('Lỗi đăng xuất:', error);
    // Ngay cả khi có lỗi, vẫn xóa local storage và coi như đã đăng xuất
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    return true;
  }
};

/**
 * Lấy thông tin người dùng hiện tại
 * @returns Thông tin người dùng
 */
export const getCurrentUser = async () => {
  try {
    // Kiểm tra token hiện tại
    if (!isTokenValid()) {
      // Thử làm mới token - refreshToken giờ có thể trả về null thay vì throw error
      const newToken = await refreshToken();
      
      // Nếu không refresh được token và không lấy được session mới
      if (!newToken && !isTokenValid()) {
        // Thử lấy session trực tiếp từ Supabase như một phương án cuối cùng
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (!sessionData.session) {
          console.warn('No active session found');
          return null;
        }
        
        // Lưu token mới vào localStorage
        localStorage.setItem('token', sessionData.session.access_token);
        localStorage.setItem('refreshToken', sessionData.session.refresh_token);
        
        // Kiểm tra lại token sau khi cập nhật từ session
        if (!isTokenValid()) {
          console.warn('Token still invalid after getting new session');
          return null;
        }
      }
    }

    // Lấy session hiện tại
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (!sessionData.session) {
      console.warn('No active session found after refresh attempt');
      return null;
    }
    
    // Lấy thông tin người dùng từ bảng users
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', sessionData.session.user.id)
      .single();
    
    if (userError) {
      console.error('Lỗi lấy thông tin người dùng:', userError);
      
      // Kiểm tra xem người dùng có tồn tại trong bảng users không
      const { data: userByEmail, error: emailError } = await supabase
        .from('users')
        .select('*')
        .eq('email', sessionData.session.user.email)
        .single();
      
      if (emailError) {
        // Nếu không tìm thấy người dùng, tạo mới trong bảng users
        console.log('Tạo mới người dùng trong bảng users');
        
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert({
            id: sessionData.session.user.id,
            email: sessionData.session.user.email,
            full_name: sessionData.session.user.user_metadata?.full_name || 'Người dùng mới',
            role: 'author',
            status: 'active',
            created_at: new Date().toISOString(),
            last_sign_in_at: new Date().toISOString()
          })
          .select();
        
        if (insertError) {
          console.error('Lỗi tạo người dùng mới:', insertError);
          return null;
        }
        
        return newUser[0];
      }
      
      // Nếu tìm thấy người dùng bằng email nhưng ID khác, cập nhật ID
      if (userByEmail && userByEmail.id !== sessionData.session.user.id) {
        console.log('Cập nhật ID người dùng');
        
        // Kiểm tra xem ID cũ có được tham chiếu trong bảng posts không
        const { data: posts, error: postsError } = await supabase
          .from('posts')
          .select('id')
          .eq('author_id', userByEmail.id);
        
        if (!postsError && posts && posts.length > 0) {
          // Cập nhật author_id trong bảng posts
          for (const post of posts) {
            await supabase
              .from('posts')
              .update({ author_id: sessionData.session.user.id })
              .eq('id', post.id);
          }
        }
        
        // Xóa bản ghi cũ
        await supabase
          .from('users')
          .delete()
          .eq('id', userByEmail.id);
        
        // Tạo bản ghi mới với ID từ auth.users
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert({
            id: sessionData.session.user.id,
            email: userByEmail.email,
            full_name: userByEmail.full_name,
            role: userByEmail.role,
            status: userByEmail.status,
            created_at: userByEmail.created_at,
            last_sign_in_at: new Date().toISOString()
          })
          .select();
        
        if (insertError) {
          console.error('Lỗi tạo người dùng mới:', insertError);
          return null;
        }
        
        return newUser[0];
      }
      
      return null;
    }
    
    return userData;
  } catch (error) {
    console.error('Lỗi lấy thông tin người dùng:', error);
    return null;
  }
};

/**
 * Quên mật khẩu
 * @param email Email của người dùng
 */
export const forgotPassword = async (email: string) => {
  try {
    // Xác định URL gốc của trang web
    const baseUrl = window.location.origin;
    
    // Tạo URL đầy đủ để chuyển hướng sau khi đặt lại mật khẩu
    const redirectUrl = `${baseUrl}/reset-password`;
    
    console.log('Reset password redirect URL:', redirectUrl);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    
    if (error) {
      console.error('Reset password error:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Lỗi quên mật khẩu:', error);
    throw error;
  }
};

/**
 * Đặt lại mật khẩu
 * @param password Mật khẩu mới
 */
export const resetPassword = async (password: string) => {
  try {
    const { error } = await supabase.auth.updateUser({
      password,
    });
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Lỗi đặt lại mật khẩu:', error);
    throw error;
  }
};

/**
 * Cập nhật thông tin người dùng
 * @param userId ID của người dùng
 * @param data Dữ liệu cập nhật
 */
export const updateUserProfile = async (userId: string, data: Partial<User>) => {
  try {
    const { error } = await supabase
      .from('users')
      .update(data)
      .eq('id', userId);
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Lỗi cập nhật thông tin người dùng:', error);
    throw error;
  }
};

/**
 * Tạo người dùng mới trong hệ thống xác thực của Supabase
 * @param email Email của người dùng
 * @param password Mật khẩu của người dùng
 * @returns Thông tin người dùng đã tạo
 */
export const createUserWithEmail = async (email: string, password: string) => {
  try {
    // Validation cơ bản
    if (!email || !email.trim()) {
      throw new Error('Email không được để trống');
    }
    
    if (!password || password.length < 6) {
      throw new Error('Mật khẩu phải có ít nhất 6 ký tự');
    }
    
    // Kiểm tra định dạng email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Định dạng email không hợp lệ');
    }
    
    // Lấy URL từ environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Missing Supabase URL');
    }
    
    // Sử dụng service key để có quyền admin
    const serviceKey = import.meta.env.VITE_SUPABASE_SERVICE_KEY;
    if (!serviceKey) {
      throw new Error('Missing Supabase service key');
    }

    console.log('Khởi tạo admin client với URL:', supabaseUrl);
    
    // Kiểm tra trực tiếp quyền admin
    try {
      // Tạo một request API đơn giản để kiểm tra quyền
      const testResponse = await fetch(`${supabaseUrl}/auth/v1/admin/users?page=1&per_page=1`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': serviceKey,
          'Authorization': `Bearer ${serviceKey}`
        }
      });
      
      if (!testResponse.ok) {
        const errorData = await testResponse.json();
        console.error('Lỗi kiểm tra quyền admin:', errorData);
        
        if (errorData.message === 'Invalid API key') {
          throw new Error('Thiếu quyền admin: API key không hợp lệ hoặc thiếu quyền');
        }
      }
    } catch (permissionError) {
      console.error('Lỗi kiểm tra quyền admin:', permissionError);
      // Không throw lỗi ở đây, vẫn thử tạo người dùng
    }
    
    // Tạo client mới với service key
    const adminClient = createClient(supabaseUrl, serviceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    });

    // Kiểm tra email đã tồn tại trong hệ thống xác thực chưa
    console.log('Kiểm tra email trong hệ thống xác thực:', email);
    const { data: userData, error: userError } = await adminClient.auth.admin.listUsers();
    
    if (userError) {
      console.error('Lỗi kiểm tra người dùng:', userError);
      
      // Kiểm tra xem có phải lỗi quyền không
      if (userError.message?.includes('unauthorized') || userError.message?.includes('Invalid API key')) {
        throw new Error('Thiếu quyền admin để tạo người dùng. Vui lòng kiểm tra lại API key.');
      }
      throw new Error(`Không thể kiểm tra người dùng: ${userError.message}`);
    }
    
    if (userData.users) {
      console.log(`Tìm thấy ${userData.users.length} người dùng trong hệ thống`);
      const existingUser = userData.users.find(u => u.email?.toLowerCase() === email.toLowerCase());
      
      if (existingUser) {
        throw new Error(`Email ${email} đã tồn tại trong hệ thống xác thực (ID: ${existingUser.id})`);
      }
    } else {
      console.warn('Không có dữ liệu users từ API, tiếp tục thử tạo người dùng');
    }

    // Kiểm tra email trong bảng users (đề phòng trường hợp auth và schema không đồng bộ)
    console.log('Kiểm tra email trong bảng users:', email);
    const { data: existingDBUser, error: dbError } = await supabase
      .from('users')
      .select('id, email')
      .eq('email', email)
      .maybeSingle();
      
    if (dbError) {
      console.warn('Lỗi kiểm tra bảng users:', dbError);
      // Không throw lỗi ở đây, vẫn tiếp tục tạo người dùng
    } else if (existingDBUser) {
      throw new Error(`Email ${email} đã tồn tại trong bảng users (ID: ${existingDBUser.id})`);
    }

    console.log('Bắt đầu tạo người dùng với email:', email);
    
    // Tạo người dùng trực tiếp thông qua REST API để có thể nhận thông tin lỗi chi tiết hơn
    try {
      console.log('Tạo người dùng qua REST API');
      
      const response = await fetch(`${supabaseUrl}/auth/v1/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': serviceKey,
          'Authorization': `Bearer ${serviceKey}`
        },
        body: JSON.stringify({
          email,
          password,
          email_confirm: true
        })
      });
      
      // Lấy thông tin chi tiết về lỗi hoặc kết quả
      const responseData = await response.json();
      
      if (!response.ok) {
        console.error('Lỗi tạo người dùng qua REST API:', responseData);
        
        // Phân tích lỗi cụ thể từ API
        if (responseData.msg?.includes('duplicate key') || 
            responseData.message?.includes('duplicate key') ||
            responseData.error?.includes('duplicate key')) {
          throw new Error(`Email ${email} đã được sử dụng trong hệ thống`);
        }
        
        if (responseData.message === 'Invalid API key' || 
            responseData.error === 'unauthorized' || 
            responseData.message?.includes('permission')) {
          throw new Error('Thiếu quyền admin để tạo người dùng. Vui lòng kiểm tra lại API key.');
        }
        
        if (responseData.message?.includes('Database error')) {
          // Nếu là lỗi database, kiểm tra xem người dùng có được tạo không mặc dù báo lỗi
          try {
            const checkResponse = await adminClient.auth.admin.listUsers();
            if (!checkResponse.error && checkResponse.data.users) {
              const justCreatedUser = checkResponse.data.users.find(u => u.email?.toLowerCase() === email.toLowerCase());
              if (justCreatedUser) {
                console.log('Tìm thấy người dùng vừa tạo dù có lỗi database:', justCreatedUser);
                return justCreatedUser;
              }
            }
          } catch (checkError) {
            console.warn('Lỗi kiểm tra người dùng sau khi nhận lỗi database:', checkError);
          }
          
          throw new Error('Lỗi cơ sở dữ liệu khi tạo người dùng. Vui lòng liên hệ quản trị viên.');
        }
        
        throw new Error(responseData.message || responseData.error || responseData.msg || 'Không thể tạo người dùng');
      }
      
      // Nếu tạo thành công
      if (responseData && responseData.id) {
        console.log('Đã tạo người dùng thành công với ID:', responseData.id);
        return responseData;
      } else {
        console.warn('Không nhận được ID người dùng từ phản hồi API:', responseData);
        
        // Kiểm tra lại bằng email
        const checkResponse = await adminClient.auth.admin.listUsers();
        if (!checkResponse.error && checkResponse.data.users) {
          const justCreatedUser = checkResponse.data.users.find(u => u.email?.toLowerCase() === email.toLowerCase());
          if (justCreatedUser) {
            console.log('Tìm thấy người dùng vừa tạo:', justCreatedUser);
            return justCreatedUser;
          }
        }
        
        throw new Error('Tạo người dùng không thành công: không nhận được thông tin người dùng');
      }
    } catch (restApiError) {
      console.error('Lỗi khi tạo người dùng qua REST API:', restApiError);
      
      // Thử lại bằng SDK
      try {
        console.log('Thử lại bằng Supabase SDK sau khi REST API thất bại');
        const { data, error } = await adminClient.auth.admin.createUser({
          email,
          password,
          email_confirm: true
        });
        
        if (error) {
          console.error('Lỗi tạo người dùng qua SDK:', error);
          throw error;
        }
        
        if (data.user) {
          console.log('Đã tạo người dùng thành công qua SDK với ID:', data.user.id);
          return data.user;
        } else {
          throw new Error('Không nhận được thông tin người dùng từ SDK');
        }
      } catch (sdkError) {
        console.error('Cả hai phương thức tạo người dùng đều thất bại:', sdkError);
        throw restApiError; // Ưu tiên throw lỗi từ REST API vì thường chi tiết hơn
      }
    }
  } catch (error) {
    console.error('Lỗi tạo người dùng:', error);
    throw error;
  }
};

/**
 * Cập nhật mật khẩu cho người dùng khác (yêu cầu quyền admin)
 * @param email Email của người dùng cần cập nhật mật khẩu
 * @param newPassword Mật khẩu mới
 */
export const updateUserPassword = async (email: string, newPassword: string) => {
  try {
    // Lấy URL từ environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Missing Supabase URL');
    }

    // Sử dụng service key để có quyền admin
    const serviceKey = import.meta.env.VITE_SUPABASE_SERVICE_KEY;
    if (!serviceKey) {
      throw new Error('Missing Supabase service key');
    }

    // Tạo client mới với service key
    const adminClient = createClient(supabaseUrl, serviceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    });

    // Tìm user bằng email
    const { data: userData, error: userError } = await adminClient.auth.admin.listUsers();
    
    if (userError) {
      throw userError;
    }
    
    const user = userData.users.find(u => u.email === email);
    
    if (!user) {
      // Thay vì tạo mới, báo lỗi người dùng không tồn tại
      throw new Error(`Không tìm thấy người dùng với email: ${email}`);
    }

    // Cập nhật mật khẩu nếu người dùng đã tồn tại
    const { error } = await adminClient.auth.admin.updateUserById(
      user.id,
      { password: newPassword }
    );

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Lỗi cập nhật mật khẩu:', error);
    throw error;
  }
};

/**
 * Đăng nhập với Google
 * @param redirectTo Đường dẫn chuyển hướng sau khi đăng nhập thành công
 * @returns Kết quả của hàm signInWithOAuth
 */
export const loginWithGoogle = async (redirectTo: string = window.location.origin) => {
  try {
    // Đảm bảo URL callback đúng định dạng
    const origin = window.location.origin;
    const callbackUrl = `${origin}/auth/callback`;
    
    console.log('🔗 Origin URL:', origin);
    console.log('🔗 Callback URL được tạo:', callbackUrl);
    
    console.log('🔄 Bắt đầu quá trình đăng nhập với Google (PKCE flow)');
    console.log('Luồng hoạt động:');
    console.log('1. Khởi tạo OAuth flow -> chuyển hướng người dùng tới Google');
    console.log('2. Người dùng đăng nhập trên Google');
    console.log('3. Google chuyển hướng về callback URL với code');
    console.log('4. Supabase xử lý code và chuyển hướng tới /auth/callback');
    console.log('5. AuthCallbackPage.tsx xử lý session và tạo/cập nhật user trong database');
    
    // Ghi log thông tin môi trường để debug
    console.log('📋 Thông tin môi trường:');
    console.log(`- URL Supabase: ${import.meta.env.VITE_SUPABASE_URL?.substring(0, 20)}...`);
    console.log(`- Môi trường: ${import.meta.env.MODE}`);
    console.log(`- Supabase client khởi tạo: ${!!supabase}`);
    
    // Kiểm tra xem đã có session hiện tại chưa
    const { data: sessionData } = await supabase.auth.getSession();
    console.log(`- Session hiện tại: ${sessionData.session ? 'Có' : 'Không'}`);
    
    // Khởi tạo OAuth flow với Supabase
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: callbackUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) {
      console.error('❌ Lỗi khởi tạo đăng nhập với Google:', error);
      console.error('Chi tiết lỗi:', JSON.stringify({
        code: error.code,
        name: error.name,
        status: error.status,
        message: error.message
      }));
      throw error;
    }

    console.log('✅ Khởi tạo OAuth thành công, chờ chuyển hướng đến Google');
    console.log('🔍 Thông tin:', {
      provider: 'google',
      url: data?.url ? `${data.url.substring(0, 50)}...` : 'unknown',
      hasUrl: !!data?.url
    });
    
    // Kiểm tra xem URL có hợp lệ không
    if (!data?.url) {
      console.error('❌ Không nhận được URL chuyển hướng từ Supabase');
      throw new Error('Không nhận được URL chuyển hướng từ Supabase');
    }
    
    // Tự động chuyển hướng sau một khoảng thời gian ngắn nếu không tự động chuyển
    setTimeout(() => {
      console.log('🔄 Tự động chuyển hướng đến Google sau timeout...');
      window.location.href = data.url;
    }, 500);
    
    return data;
  } catch (error) {
    console.error('❌ Lỗi đăng nhập với Google:', error);
    
    // Log chi tiết lỗi nếu có
    if (error instanceof Error) {
      console.error('Chi tiết lỗi:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
    }
    
    // Các bước khắc phục lỗi
    console.log('⚠️ Các bước có thể thử để khắc phục:');
    console.log('1. Xóa cache và cookie của trình duyệt');
    console.log('2. Đảm bảo đã cấu hình đúng URL callback trong Supabase Authentication');
    console.log('3. Kiểm tra cài đặt Google OAuth trong Supabase');
    console.log('4. Thử lại với trình duyệt khác');
    
    throw error;
  }
};

// Re-export useAuth hook từ hooks để tiện lợi hơn
export { useAuth } from '../hooks/useAuth'; 